import {useTranslation} from "react-i18next";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useEffect, useState} from "react";
import {ProjectApi} from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import RoleEntry from "./RoleEntry.jsx";
import Modal from "../../../components/Modal.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import YesNo from "../../../components/YesNo.jsx";
import RolePermissions from "./RolePermissions.jsx";
import {useStringQueryState} from "../../../utils/useQueryState.js";
import MediumSecureLayout from "../../../layout/MediumSecureLayout.jsx";
import Button from "../../../components/Button.jsx";

export default function ProjectRoles({project}) {
    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let [refresh, setRefresh] = useState(false);
    let [roles, setRoles] = useState();
    let [currentRoleId, setCurrentRoleId] = useStringQueryState('roleId', null);

    let authorityConfiguration = useAuthorityConfiguration();

    useEffect(() => {
        if(refresh) {
            setRefresh(false);
            return;
        }

        let active = true;
        setRoles(undefined);
        new ProjectApi(authorityConfiguration)
            .projectRoles({
                projectId: project.id,
            })
            .then((roles) => {
                if (!active) return;
                if (roles?.length > 0) {
                    setRoles(roles);
                    setCurrentRoleId(roles[0].id);
                } else {
                    setRoles(null);
                    setCurrentRoleId(null);
                }
            })
            .catch((e) => {
                if (active) et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => { active = false; };
    }, [project.id, refresh]);


    let [createRoleModalOpened, setCreateRoleModalOpened] = useState(false);
    let [createRoleButtonClicked, setCreateRoleButtonClicked] = useState(false);
    let [createRoleName, setCreateRoleName] = useState('');

    useEffect(() => {
        setCreateRoleButtonClicked(false);
        if(createRoleButtonClicked === true) {
            new ProjectApi(authorityConfiguration)
                .createProjectRole({
                    projectId: project.id,
                    createRoleInput: {
                        name: createRoleName,
                    }
                })
                .then(() => {
                    setCreateRoleName('');
                    setCreateRoleModalOpened(false);
                    setRefresh(true);
                }).catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
        }

    }, [createRoleButtonClicked]);

    let [deleteRoleId, setDeleteRoleId] = useState(null);
    let [deleteModalOpen, setDeleteModalOpen] = useState(false);
    let [deleteRoleConfirmed, setDeleteRoleConfirmed] = useState(false);

    useEffect(() => {

        if(deleteRoleConfirmed === true && deleteRoleId !== null) {

            new ProjectApi(authorityConfiguration)
                .deleteProjectRole({
                    projectId: project.id,
                    roleId: deleteRoleId,
                })
                .then(() => {
                    setDeleteRoleId(null);
                    setDeleteModalOpen(false);
                    setRefresh(true);
                }).catch((e) => et(e).then((message) => { if (message) toast.error(message); }))

        }

        setDeleteRoleConfirmed(false);

    }, [deleteRoleConfirmed]);

    const CreateRoleButton = () => (
        <Button
            onClick={() => {
                setCreateRoleName('');
                setCreateRoleModalOpened(true);
            }}
        >
            {t('project.create-role')}
        </Button>
    );

    return [
        <YesNo
            key={'projectRolesYesNo'}
            visible={deleteModalOpen}
            setVisible={setDeleteModalOpen}
            title={t('project.really-delete-role')}
            onYes={() => setDeleteRoleConfirmed(true)}
        />,
        <Modal
            key={'projectRolesCreateModal'}
            visible={createRoleModalOpened}
            setVisible={setCreateRoleModalOpened}
            title={t('project.new-role-name')}
            className={'max-w-lg'}
        >
            <input
                type={"text"}
                value={createRoleName}
                onChange={(e) => setCreateRoleName(e.target.value)}
                className={'form-input w-10/12'}
            />
            <Button className={'mt-2'} onClick={() => setCreateRoleButtonClicked(true)}>
                {t('project.create-role')}
            </Button>
        </Modal>,
        <MediumSecureLayout
            title={t('route.project-user-roles')}
            actions={CreateRoleButton()}
            key={'projectRolesLayout'}
        >
            <LoadingComponent loading={roles === undefined} loadedClassName={''}>
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl mb-8">
                    <div className="flex flex-col md:flex-row md:-mr-px">
                        <div
                            className="flex flex-nowrap overflow-x-scroll no-scrollbar md:block md:overflow-auto px-3 py-6 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700/60 min-w-60 md:space-y-3"
                        >
                            {roles?.length > 0 && (
                                <div>
                                    <div
                                        className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-3">
                                        {t('project.roles')}
                                    </div>

                                    <ul className="flex flex-nowrap md:block mr-3 md:mr-0">
                                        {Object.values(roles ?? {}).map((role) => {
                                            return <RoleEntry
                                                key={'entry-' + role.id}
                                                onClick={() => {
                                                    setCurrentRoleId(role.id);
                                                }}
                                                selected={currentRoleId === role.id}
                                            >
                                                {role.name}
                                            </RoleEntry>
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="grow">
                            <div className="space-y-6">
                                <RolePermissions
                                    project={project}
                                    roleId={currentRoleId}
                                    onChange={() => setRefresh(true)}
                                    onDelete={(rid) => {
                                        setDeleteRoleId(rid);
                                        setDeleteModalOpen(true);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </LoadingComponent>
        </MediumSecureLayout>
    ];
}
